import type { Locale } from "./Locale";

import { buildI18n } from "./";
import { emptyCatalog } from "./Catalog";
import { LocaleProvider } from "./LocaleContext";
import { TextDirectionProvider } from "../../mads/util/TextDirection";

import Gettext, { type GettextInterface } from "@indeed/indeed-gettext";
import { createContext, useContext, useMemo } from "react";

/**
 * Determine the text direction based on the locale
 *
 * Arabic and Hebrew are RTL languages, everything else is LTR.
 */
export function textDirectionFromLocale(locale: Locale): "ltr" | "rtl" {
  const language = locale.language;
  return language === "ar" || language === "he" ? "rtl" : "ltr";
}

export const GettextContext = createContext<GettextInterface>(Gettext(emptyCatalog()));

export function useI18n(): GettextInterface {
  return useContext(GettextContext);
}

/**
 * Provides common I18n-related context to the app
 *
 * Specifically the gettext instance, the locale and the text direction.
 */
export function I18nProvider({ children, locale }: { children: React.ReactNode; locale: Locale }) {
  const i18n = useMemo(() => buildI18n(locale), [locale]);
  const textDirection = textDirectionFromLocale(locale);
  return (
    <GettextContext.Provider value={i18n}>
      <LocaleProvider locale={locale}>
        <TextDirectionProvider textDirection={textDirection}>{children}</TextDirectionProvider>
      </LocaleProvider>
    </GettextContext.Provider>
  );
}
